import React, { useState, useEffect, useRef } from 'react';
import './ChatInterface.css';
import { callDifyChat } from '../api/dify';

/**
 * AI客服聊天界面组件
 * 基于妙多设计实现的聊天界面
 */
interface ChatInterfaceProps {
  onClose?: () => void;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({ onClose }) => {
  const [message, setMessage] = useState('');
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lastRequestTime, setLastRequestTime] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageIdRef = useRef(7); // 用于生成唯一消息ID
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      content: '你好！我是AI客服小迎，很高兴为你服务。请问有什么可以帮助你的吗？'
    },
    {
      id: 2,
      type: 'user',
      content: '你们有什么特色服务吗？'
    },
    {
      id: 3,
      type: 'bot',
      content: `当然有！我们为您提供多项贴心服务：

🎯 **智能推荐**：根据您的喜好，为您精准推荐心仪商品

⚡ **极速配送**：同城2小时达，让等待变成惊喜

🛡️ **品质保障**：7天无理由退换，30天质量保证

💎 **会员专享**：积分兑换、生日特权、专属客服

还有什么想了解的吗？我随时为您服务！`
    }
  ]);

  /**
   * 滚动到消息底部
   */
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  /**
   * 当消息更新时自动滚动到底部
   */
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  /**
   * 调用Dify API获取AI回复
   */
  const callDifyAPI = async (userMessage: string, currentConversationId?: string | null): Promise<{ answer: string; conversationId: string }> => {
    try {
      // 使用传入的conversationId或当前状态中的conversationId
      const useConversationId = currentConversationId !== undefined ? currentConversationId : conversationId;
      // 静默处理会话ID
      
      const response = await callDifyChat({
        query: userMessage,
        conversation_id: useConversationId,
        user: 'user-' + Date.now()
      });
      // API响应处理完成
      
      return {
        answer: response.answer,
        conversationId: response.conversation_id
      };
    } catch (error) {
      console.error('Dify API调用错误:', error);
      
      // 如果是会话不存在的错误，清空会话ID并重试一次
      if (error instanceof Error && error.message.includes('Conversation Not Exists')) {
        console.log('会话已失效，重置会话ID并重试');
        
        try {
          // 重试请求，不使用会话ID
          const retryResponse = await callDifyChat({
            query: userMessage,
            conversation_id: null, // null 表示新会话
            user: 'user-' + Date.now()
          });
          
          return {
            answer: retryResponse.answer,
            conversationId: retryResponse.conversation_id
          };
        } catch (retryError) {
          console.error('重试失败:', retryError);
          throw retryError;
        }
      }
      
      throw error; // 重新抛出错误，让上层处理
    }
  };

  /**
   * 防恶意刷检查
   */
  const checkRateLimit = (): boolean => {
    const now = Date.now();
    const timeDiff = now - lastRequestTime;
    
    // 限制每2秒只能发送一条消息
    if (timeDiff < 2000) {
      return false;
    }
    
    setLastRequestTime(now);
    return true;
  };

  /**
   * 显示用户友好的错误提示
   */
  const getErrorMessage = (error: any): string => {
    const errorMessage = error?.message || error?.toString() || '';
    
    if (errorMessage.includes('Conversation Not Exists') || errorMessage.includes('会话不存在')) {
      return '会话已重置，请重新开始对话。';
    }
    
    if (errorMessage.includes('请求过于频繁')) {
      return '您的请求过于频繁，请稍后再试。';
    }
    
    if (errorMessage.includes('重复的消息')) {
      return '请不要发送重复的消息，换个问题试试吧。';
    }
    
    if (errorMessage.includes('消息长度')) {
      return '消息太长了，请简化您的问题。';
    }
    
    if (errorMessage.includes('不允许的内容')) {
      return '消息包含不合适的内容，请重新输入。';
    }
    
    if (errorMessage.includes('网络') || errorMessage.includes('fetch')) {
      return '网络连接异常，请检查网络后重试。';
    }
    
    if (errorMessage.includes('401') || errorMessage.includes('unauthorized')) {
      return 'API密钥无效，请联系管理员。';
    }
    
    if (errorMessage.includes('429')) {
      return '服务器繁忙，请稍后再试。';
    }
    
    if (errorMessage.includes('500')) {
      return '服务器内部错误，请稍后再试。';
    }
    
    return '抱歉，服务暂时不可用，请稍后再试。';
  };

  /**
   * 发送消息处理函数
   */
  const handleSendMessage = async () => {
    const userMessage = message.trim();
    // 开始发送消息
    
    // 基本验证
    if (!userMessage || isLoading) {
      // 消息验证失败或正在加载中
      return;
    }
    
    // 防恶意刷检查
    if (!checkRateLimit()) {
      alert('请不要频繁发送消息，请等待2秒后再试。');
      return;
    }
    
    // 消息长度限制
    if (userMessage.length > 500) {
      alert('消息长度不能超过500个字符。');
      return;
    }
    
    // 设置加载状态
    setIsLoading(true);
    setMessage('');
    
    // 生成唯一ID
    const userMessageId = messageIdRef.current++;
    const botMessageId = messageIdRef.current++;
    
    // 添加用户消息
    const newUserMessage = {
      id: userMessageId,
      type: 'user' as const,
      content: userMessage
    };
    
    // 添加加载状态的机器人消息
    const loadingMessage = {
      id: botMessageId,
      type: 'bot' as const,
      content: '正在思考中...'
    };
    
    setMessages(prev => [...prev, newUserMessage, loadingMessage]);
    
    try {
      // 调用Dify API获取回复
      const apiResult = await callDifyAPI(userMessage, conversationId);
      
      // 更新会话ID
        if (apiResult.conversationId && apiResult.conversationId !== conversationId) {
          setConversationId(apiResult.conversationId);
        }
      
      // 更新机器人回复
      const botReply = {
        id: botMessageId,
        type: 'bot' as const,
        content: apiResult.answer
      };
      
      setMessages(prev => {
        const newMessages = [...prev];
        const lastIndex = newMessages.findIndex(msg => msg.id === botMessageId);
        if (lastIndex !== -1) {
          newMessages[lastIndex] = botReply;
        }
        return newMessages;
      });
    } catch (error) {
      console.error('发送消息失败:', error);
      
      // 获取友好的错误提示
      const friendlyErrorMessage = getErrorMessage(error);
      
      // 更新为错误消息
      const errorMessage = {
        id: botMessageId,
        type: 'bot' as const,
        content: friendlyErrorMessage
      };
      
      setMessages(prev => {
        const newMessages = [...prev];
        const lastIndex = newMessages.findIndex(msg => msg.id === botMessageId);
        if (lastIndex !== -1) {
          newMessages[lastIndex] = errorMessage;
        }
        return newMessages;
      });
    } finally {
      // 重置加载状态
      setIsLoading(false);
    }
  };

  /**
   * 处理回车键发送
   */
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  return (
    <div className="chat-interface">
      {/* 聊天头部 */}
      <div className="chat-header">
        <div className="chat-avatar-container">
          <div className="chat-avatar">
            <img 
              src="https://miaoduo.fbcontent.cn/private/resource/image/1995f9df7a39834-8f42c32f-e3f8-47ba-bd27-45c995b4781b.jpeg" 
              alt="AI客服小迎" 
            />
          </div>
          <div className="close-button" onClick={onClose} style={{ cursor: 'pointer' }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M12 4L4 12M4 4L12 12" stroke="white" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
        </div>
        <div className="chat-title">AI客服员工—小迎</div>
        <div className="chat-subtitle">
          你从回答客户常问问题到遵守平台规则，小迎bot都能轻松应对，一应俱全。
        </div>
      </div>

      {/* 聊天消息区域 */}
      <div className="chat-messages">
        {messages.map((msg) => (
          <div key={msg.id} className={`message ${msg.type}`}>
            {msg.type === 'bot' && (
              <div className="bot-avatar">
                <img 
                  src="https://miaoduo.fbcontent.cn/private/resource/image/1995f9df7a39834-8f42c32f-e3f8-47ba-bd27-45c995b4781b.jpeg" 
                  alt="AI客服小迎"
                  style={{ width: '32px', height: '32px', borderRadius: '50%' }}
                />
              </div>
            )}
            <div className="message-content">
              {msg.content.split('\n').map((line, index) => (
                <React.Fragment key={index}>
                  {line}
                  {index < msg.content.split('\n').length - 1 && <br />}
                </React.Fragment>
              ))}
            </div>
            {msg.type === 'user' && (
              <div className="user-avatar">
                <span>我</span>
              </div>
            )}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* 输入区域 */}
      <div className="chat-input-area">
        <div className="input-container">
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="请输入您的问题..."
            className="message-input"
          />
        </div>
        <button 
          onClick={handleSendMessage}
          className="send-button"
          disabled={isLoading || !message.trim()}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M18 2L9 11M18 2L12 18L9 11M18 2L2 8L9 11" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
    </div>
  );
};

export default ChatInterface;